import React, { useEffect, useState, useCallback } from "react";
import { StyleSheet, View, KeyboardAvoidingView } from "react-native";
import { Input, Button, ErrorDialog, Container } from "src/components";
import { IError } from "src/components/dialog/error";
import { useForm } from "react-hook-form";
import * as formValidators from "src/utils/form-validator";
import { localizationStrings } from "./constants";
import { hideKeyboard } from "src/utils/event";
import { useRecoilState } from "recoil";
import { passwordResetEmailState } from "../auth-password-reset/recoil";
import { useNavigation, useRoute, useLinkTo } from "@react-navigation/native";
import { RootStackScreenProps } from "src/navigation/types";
import { useSignIn } from "src/hooks";
import LocalText from "src/local";

import DialogButton from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

type FormValues = {
  email: string;
  password: string;
};

export type AuthSignInScreenNavigationParam =
  | undefined
  | {
      redirect?: string;
    };

export const AuthSignInScreen: React.FC = () => {
  const navigation =
    useNavigation<RootStackScreenProps<"SignIn">["navigation"]>();
  const route = useRoute<RootStackScreenProps<"SignIn">["route"]>();
  const linkTo = useLinkTo();
  const {
    register,
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { email: "", password: "" },
  });
  const [error, setError] = useState<IError | null>(null);
  const [processed, setProcessed] = useState<boolean>(false);
  const authSignIn = useSignIn();

  const [, setPasswordResetEmail] = useRecoilState<string>(
    passwordResetEmailState
  );
  const [showUserNotConfirmedDialog, setShowUserNotConfirmedDialog] =
    useState<boolean>(false);
  useEffect(() => {
    setPasswordResetEmail("");
  }, [setPasswordResetEmail]);
  useEffect(() => {
    register("email", {
      validate: (value) => {
        if (!formValidators.RequireValidator(value)) {
          return localizationStrings.inputEmailValidationRequire;
        }
        if (!formValidators.EmailValidator(value)) {
          return localizationStrings.inputEmailValidationFormat;
        }
        return true;
      },
    });
    register("password", {
      validate: (value) => {
        if (!formValidators.RequireValidator(value)) {
          return localizationStrings.inputPasswordValidationRequire;
        }
        // TODO: Add password pattern
        return true;
      },
    });
  }, [register]);
  const onSignIn = useCallback(
    async (data: FormValues) => {
      hideKeyboard();
      setProcessed(true);
      try {
        await authSignIn(data.email, data.password);
        setProcessed(false);
        if (route.params?.redirect && route.params.redirect.length > 0) {
          linkTo(route.params.redirect);
        } else {
          navigation.navigate("Main", { screen: "Search" });
        }
      } catch (e: any) {
        if (e.code === "NotAuthorizedException") {
          const authError = new Error(
            LocalText.screens.authSignIn.errorNotAuthorizedException
          );
          console.error(authError);
          setError(authError);
        } else if (e.code === "UserNotConfirmedException") {
          setShowUserNotConfirmedDialog(true);
        } else {
          console.error(e);
          setError(e as Error);
        }
        setProcessed(false);
      }
    },
    [authSignIn, linkTo, route.params?.redirect]
  );
  return (
    <Container loading={processed}>
      <KeyboardAvoidingView>
        <View style={styles.container}>
          <Input
            placeholder={localizationStrings.inputEmailPlaceholder}
            name={"email"}
            control={control}
            error={errors.email?.message}
            keyboardType={"email-address"}
            autoCapitalize={"none"}
          />
          <Input
            placeholder={localizationStrings.inputPasswordPlaceholder}
            name={"password"}
            control={control}
            error={errors.password?.message}
            secureTextEntry={true}
            autoCapitalize={"none"}
          />
          <View style={styles.buttonContainer}>
            <Button
              title={localizationStrings.btnSignIn}
              loading={processed}
              onPress={handleSubmit(onSignIn)}
            />
            <Button
              title={localizationStrings.btnRestore}
              variant={"secondary"}
              onPress={() => {
                setPasswordResetEmail(getValues("email") || "");
                setTimeout(() => navigation.navigate("PasswordReset"), 500); // TODO: Rebild to better async flow
              }}
            />
          </View>
        </View>
      </KeyboardAvoidingView>
      {error ? (
        <ErrorDialog
          error={error}
          visible={true}
          onClose={() => setError(null)}
        />
      ) : null}
      <Dialog
        open={showUserNotConfirmedDialog}
        onClose={() => {
          setShowUserNotConfirmedDialog(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {`このアカウントは登録が未完了です。`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            sx={{ whiteSpace: "pre" }}
          >
            {`「問い合わせ」ボタンより、以下内容を送信ください。\n\n件名：登録未完了\nメールアドレス：ご登録のメールアドレス\nお問合せ内容：登録未完了\n※対応次第、メールでお知らせします。\n（対応に数日程度かかる場合がございます）`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <DialogButton
            onClick={() => {
              setShowUserNotConfirmedDialog(false);
              window.open(`https://choku.me/contact`, "_blank");
            }}
            autoFocus
          >
            問い合わせ
          </DialogButton>
          <DialogButton
            onClick={() => {
              setShowUserNotConfirmedDialog(false);
            }}
          >
            キャンセル
          </DialogButton>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
  },
  buttonContainer: {
    flexDirection: "column",
    marginTop: 36,
    marginHorizontal: 16,
  },
});
